import { z } from 'zod';

/**
 * Validated, typed access to build-time environment variables.
 *
 * Service URLs use one configured API host and centrally owned service
 * ports. Application URLs remain independently configurable.
 *
 * All client-exposed variables must be prefixed with `VITE_`. Validation runs
 * once at module load so a misconfigured environment fails fast and loudly.
 */
const optionalHost = z
  .string()
  .optional()
  .transform((value) => (value && value.trim().length > 0 ? value.trim().replace(/\/$/, '') : undefined))
  .pipe(
    z
      .string()
      .url()
      .refine((value) => {
        const parsed = new URL(value);
        return !parsed.port && parsed.pathname === '/' && !parsed.search && !parsed.hash;
      }, 'VITE_API_HOST must be an origin without a port or path.')
      .optional(),
  );

const optionalUrl = z
  .string()
  .optional()
  .transform((value) => (value && value.trim().length > 0 ? value.trim() : undefined))
  .pipe(z.string().url().optional());

const serviceConfig = {
  identity: { path: '/identity', prefix: '/api/v1' },
  people: { path: '/people', prefix: '/api/v1' },
  hrms: { path: '/hrms', prefix: '/api/v1' },
  finance: { path: '/finance', prefix: '/api/v1' },
  audit: { path: '/audit', prefix: '/api/v1' },
  portal: { path: '/portal', prefix: '/api/v1' },
} as const;

const envSchema = z.object({
  VITE_API_HOST: optionalHost,
  VITE_HRMS_APP_URL: optionalUrl,
  VITE_FINANCE_APP_URL: optionalUrl,
  VITE_PORTAL_APP_URL: optionalUrl,
  VITE_APP_NAME: z.string().min(1).default('HEXAUNITY'),
  VITE_APP_ENV: z.enum(['development', 'staging', 'production']).default('development'),
  VITE_ENABLE_DEVTOOLS: z
    .string()
    .optional()
    .transform((value) => value === 'true'),
  VITE_API_TIMEOUT: z.coerce.number().int().positive().default(30_000),
});

const parsed = envSchema.safeParse(import.meta.env);

if (!parsed.success) {
  console.error('❌ Invalid environment configuration:', parsed.error.flatten().fieldErrors);
  throw new Error('Invalid environment configuration. See console for details.');
}

const data = parsed.data;

const apiHost = data.VITE_API_HOST ?? (import.meta.env.DEV ? 'http://localhost' : undefined);
if (!apiHost) {
  throw new Error('VITE_API_HOST must be set for non-development builds.');
}

const serviceUrl = ({
  path,
  prefix,
}: (typeof serviceConfig)[keyof typeof serviceConfig]) =>
  `${apiHost}${path}${prefix}`;


export const env = {
  services: {
    identity: serviceUrl(serviceConfig.identity),
    people: serviceUrl(serviceConfig.people),
    hrms: serviceUrl(serviceConfig.hrms),
    finance: serviceUrl(serviceConfig.finance),
    audit: serviceUrl(serviceConfig.audit),
    portal: serviceUrl(serviceConfig.portal),
  },
  appUrls: {
    HRMS: data.VITE_HRMS_APP_URL,
    FINANCE: data.VITE_FINANCE_APP_URL,
    EMPLOYEE_PORTAL: data.VITE_PORTAL_APP_URL,
  },
  appName: data.VITE_APP_NAME,
  appEnv: data.VITE_APP_ENV,
  enableDevtools: data.VITE_ENABLE_DEVTOOLS ?? false,
  apiTimeout: data.VITE_API_TIMEOUT,
  isDevelopment: data.VITE_APP_ENV === 'development',
  isProduction: data.VITE_APP_ENV === 'production',
} as const;

export type Env = typeof env;
export type ServiceName = keyof typeof env.services;
